import type { StoredDocument } from './types';

/**
 * The documents being worked on, held in the browser.
 *
 * Files stay on the device: nothing is uploaded except when Office conversion
 * is needed, which is the one operation that requires LibreOffice on the
 * server. Everything is mirrored into IndexedDB so an installed app that is
 * relaunched — or a tab that is reloaded — picks up where it left off.
 */

const DB_NAME = 'pdf-toolkit';
const DB_VERSION = 1;
const STORE = 'documents';

interface Record extends StoredDocument {
  bytes: Uint8Array;
}

const memory = new Map<string, Record>();

function openDb(): Promise<IDBDatabase | null> {
  return new Promise((resolve) => {
    let request: IDBOpenDBRequest;
    try {
      request = indexedDB.open(DB_NAME, DB_VERSION);
    } catch {
      // Private mode or blocked storage: run from memory only.
      resolve(null);
      return;
    }
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE, { keyPath: 'id' });
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => resolve(null);
  });
}

async function withStore<T>(
  mode: IDBTransactionMode,
  run: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T | null> {
  const db = await openDb();
  if (!db) return null;
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(STORE, mode);
      const request = run(tx.objectStore(STORE));
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => resolve(null);
      tx.oncomplete = () => db.close();
    } catch {
      resolve(null);
    }
  });
}

/** Loads anything persisted by a previous visit. Safe to call more than once. */
export async function hydrate(): Promise<StoredDocument[]> {
  const rows = (await withStore<Record[]>('readonly', (store) => store.getAll())) ?? [];
  for (const row of rows) {
    // getAll returns structured clones; normalize back to Uint8Array.
    memory.set(row.id, { ...row, bytes: new Uint8Array(row.bytes) });
  }
  return list();
}

export function list(): StoredDocument[] {
  return [...memory.values()]
    .map(({ bytes: _bytes, ...meta }) => meta)
    .sort((a, b) => a.createdAt - b.createdAt);
}

export function bytesFor(docId: string): Uint8Array | undefined {
  return memory.get(docId)?.bytes;
}

export async function add(record: Omit<Record, 'id' | 'createdAt'>): Promise<StoredDocument> {
  const full: Record = { ...record, id: crypto.randomUUID(), createdAt: Date.now() };
  memory.set(full.id, full);
  await withStore('readwrite', (store) => store.put(full));
  const { bytes: _bytes, ...meta } = full;
  return meta;
}

export async function remove(docId: string): Promise<void> {
  memory.delete(docId);
  await withStore('readwrite', (store) => store.delete(docId));
}

export async function clear(): Promise<void> {
  memory.clear();
  await withStore('readwrite', (store) => store.clear());
}

export function count(): number {
  return memory.size;
}
